import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { UserAuth } from "../../../context/AuthContext";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import ImageUploader from "../ImageUploader/ImageUploader";
import './AdminForm.css';

const emptyImage = () => ({ key: Math.random().toString(36).slice(2), url: null, path: null, name: null, alt: '' });

const ProjectForm = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const { user } = UserAuth();

    const [title, setTitle] = useState('');
    const [bodyHtml, setBodyHtml] = useState('');
    const [images, setImages] = useState([emptyImage()]);
    const [videoFile, setVideoFile] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            const snap = await getDoc(doc(db, "project_reports", id));
            if (snap.exists()) {
                const data = snap.data();
                setTitle(data.title || '');
                setBodyHtml(data.bodyHtml || '');
                const existingImages = (data.images || []).map(img => ({ ...emptyImage(), ...img }));
                setImages(existingImages.length ? existingImages : [emptyImage()]);
                if (data.videoUrl) {
                    if (data.videoUrl.includes('firebasestorage')) {
                        setVideoFile({ url: data.videoUrl, path: null });
                    } else {
                        setVideoUrl(data.videoUrl);
                    }
                }
            }
            setLoading(false);
        })();
    }, [id, isEdit]);

    const updateImage = (key, patch) => {
        setImages(prev => prev.map(img => img.key === key ? { ...img, ...patch } : img));
    };

    const removeImage = (key) => {
        setImages(prev => prev.filter(img => img.key !== key));
    };

    const addImageSlot = () => setImages(prev => [...prev, emptyImage()]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const cleanImages = images
                .filter(img => img.url)
                .map(({ url, path, alt }) => ({ url, path: path || null, alt: alt || '' }));

            const payload = {
                title: title.trim(),
                bodyHtml,
                images: cleanImages,
                videoUrl: videoFile?.url || videoUrl.trim() || null,
                updatedAt: serverTimestamp(),
                authorEmail: user?.email || null,
            };

            if (isEdit) {
                await updateDoc(doc(db, "project_reports", id), payload);
            } else {
                await addDoc(collection(db, "project_reports"), { ...payload, createdAt: serverTimestamp() });
            }
            navigate('/userarea');
        } catch (err) {
            setError('Speichern fehlgeschlagen: ' + err.message);
            setSaving(false);
        }
    };

    if (loading) return <div className="admin-form-page"><p>Lädt…</p></div>;

    return (
        <div className="admin-form-page">
            <h1>{isEdit ? 'Projektbericht bearbeiten' : 'Neuer Projektbericht'}</h1>
            <form onSubmit={handleSubmit} className="admin-form">
                <label className="admin-form-field">
                    <span>Titel</span>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        placeholder="z. B. Kurzbericht: Wasserableitung am Trainingscenter"
                    />
                </label>

                <label className="admin-form-field">
                    <span>Beschreibung</span>
                    <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Was wurde gemacht, was ist der Nutzen?" />
                </label>

                <div className="admin-form-field">
                    <span>Bilder</span>
                    <div className="admin-form-gallery">
                        {images.map((img) => (
                            <div className="admin-form-gallery-item" key={img.key}>
                                <ImageUploader
                                    folder="project_reports"
                                    accept="image/*"
                                    label=""
                                    value={img.url ? img : null}
                                    onUploaded={(uploaded) => updateImage(img.key, uploaded)}
                                    onRemove={() => updateImage(img.key, { url: null, path: null, name: null })}
                                />
                                {img.url && (
                                    <input
                                        type="text"
                                        className="admin-form-alt-input"
                                        placeholder="Bildbeschreibung (Alt-Text)"
                                        value={img.alt}
                                        onChange={(e) => updateImage(img.key, { alt: e.target.value })}
                                    />
                                )}
                                {images.length > 1 && (
                                    <button type="button" className="admin-form-remove-slot" onClick={() => removeImage(img.key)}>
                                        Bild entfernen
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    <button type="button" className="admin-form-add-btn" onClick={addImageSlot}>
                        + Weiteres Bild
                    </button>
                </div>

                <div className="admin-form-field">
                    <span>Video (optional)</span>
                    <ImageUploader
                        folder="project_reports"
                        accept="video/*"
                        label="Videodatei hochladen"
                        value={videoFile}
                        onUploaded={setVideoFile}
                        onRemove={() => setVideoFile(null)}
                    />
                    {!videoFile && (
                        <input
                            type="text"
                            className="admin-form-url-input"
                            placeholder="…oder externen Video-Link einfügen (z. B. YouTube)"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                        />
                    )}
                </div>

                {error && <p className="admin-form-error">{error}</p>}

                <div className="admin-form-actions">
                    <button type="button" className="admin-form-btn admin-form-btn--outline" onClick={() => navigate('/userarea')}>
                        Abbrechen
                    </button>
                    <button type="submit" className="admin-form-btn admin-form-btn--primary" disabled={saving}>
                        {saving ? 'Speichert…' : 'Speichern'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProjectForm;
